<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { FileText } from 'lucide-vue-next'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'
import ReportListItem from '@/components/report/ReportListItem.vue'
import { groupReportsByDay } from '@/utils/datetime'

const store = useStore()
const settings = computed(() => store.getters['user/getUserSettings'] || { tz: 'UTC' })

const props = defineProps({
  reports: {
    type: Array,
    default: () => []
  },
  activeReport: {
    type: Object,
    default: null
  }
})

const groupedReports = computed(() => groupReportsByDay(props.reports, settings.value.tz))

const emit = defineEmits(['select', 'delete'])

// Delete confirmation state
const confirmDialog = ref(null)
const reportToDelete = ref(null)

const handleSelect = (report) => {
  emit('select', report)
}

const handleDeleteClick = (report) => {
  reportToDelete.value = report
  confirmDialog.value?.open()
}

const handleDeleteConfirm = () => {
  if (reportToDelete.value) {
    emit('delete', reportToDelete.value)
    reportToDelete.value = null
  }
}

const handleDeleteCancel = () => {
  reportToDelete.value = null
}
</script>

<template>
  <div class="flex-1 overflow-y-auto">
    <!-- Empty State -->
    <div v-if="reports.length === 0" class="p-6 text-center text-muted-foreground text-sm">
      <FileText class="h-8 w-8 mx-auto mb-3 opacity-50" />
      <p>No reports yet</p>
    </div>

    <!-- Reports List, grouped by day -->
    <div v-else>
      <div v-for="group in groupedReports" :key="group.key">
        <div class="px-4 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/40 sticky top-0">
          {{ group.label }}
        </div>
        <div class="divide-y divide-border">
          <ReportListItem
            v-for="report in group.items"
            :key="report.report_uuid"
            :report="report"
            :is-active="activeReport?.report_uuid === report.report_uuid"
            :timezone="settings.tz"
            @select="handleSelect"
            @delete="handleDeleteClick"
          />
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Dialog -->
    <DialogConfirm
      ref="confirmDialog"
      title="Delete Report"
      :description="`Are you sure you want to delete '${reportToDelete?.title || 'this report'}'? This action cannot be undone.`"
      confirm-text="Delete"
      variant="error"
      @confirm="handleDeleteConfirm"
      @cancel="handleDeleteCancel"
    />
  </div>
</template>
